import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

const instances = new WeakMap();
let observer = null;
let updateFrame = null;
let nestedWheelHandlerAttached = false;
const HORIZONTAL_SCROLLBAR_EXCLUDED_SELECTOR = [
    'html',
    'body',
    'input',
    'textarea',
    'select',
    '.tox',
    '.nested-table',
].join(',');
const TABULATOR_HORIZONTAL_SPACER_CLASS = 'tabulator-horizontal-scroll-spacer';
const HORIZONTAL_OVERFLOW_TOLERANCE = 2;

function shouldUseCustomScrollbar(tableholder) {
    return !tableholder.closest('.nested-table');
}

function getElementWidth(element) {
    if (!element) return 0;

    return Math.max(
        element.scrollWidth || 0,
        Math.ceil(element.getBoundingClientRect().width),
    );
}

function hasTabulatorHorizontalOverflow(tableholder) {
    const tabulator = tableholder.closest('.tabulator');
    const table = tableholder.querySelector(':scope > .tabulator-table');
    const header = tabulator?.querySelector(
        '.tabulator-header .tabulator-headers',
    );
    const contentWidth = Math.max(
        getElementWidth(table),
        getElementWidth(header),
    );

    if (contentWidth) {
        return (
            contentWidth >
            tableholder.clientWidth + HORIZONTAL_OVERFLOW_TOLERANCE
        );
    }

    return (
        tableholder.scrollWidth >
        tableholder.clientWidth + HORIZONTAL_OVERFLOW_TOLERANCE
    );
}

function updateTabulatorHorizontalScrollClass(tableholder) {
    const hasHorizontalScroll = hasTabulatorHorizontalOverflow(tableholder);
    let spacer = tableholder.querySelector(
        `:scope > .${TABULATOR_HORIZONTAL_SPACER_CLASS}`,
    );

    tableholder.classList.toggle(
        'tabulator-has-horizontal-scroll',
        hasHorizontalScroll,
    );

    if (hasHorizontalScroll && !spacer) {
        spacer = document.createElement('div');
        spacer.className = TABULATOR_HORIZONTAL_SPACER_CLASS;
        tableholder.appendChild(spacer);
    } else if (!hasHorizontalScroll && spacer) {
        spacer.remove();
    }
}

function createPerfectScrollbar(element, options = {}) {
    element.classList.add('custom-perfect-scrollbar');
    instances.set(
        element,
        new PerfectScrollbar(element, {
            minScrollbarLength: 36,
            wheelPropagation: false,
            ...options,
        }),
    );
}

function isNativeHorizontalScrollable(element) {
    if (element.closest(HORIZONTAL_SCROLLBAR_EXCLUDED_SELECTOR)) return false;
    if (element.closest('.tabulator .tabulator-tableholder')) return false;
    if (element.closest('.ps')) return false;

    const style = window.getComputedStyle(element);
    const canScrollHorizontally = ['auto', 'scroll'].includes(style.overflowX);
    const canScrollVertically = ['auto', 'scroll'].includes(style.overflowY);
    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth + 1;
    const hasVerticalOverflow = element.scrollHeight > element.clientHeight + 1;

    return (
        canScrollHorizontally &&
        hasHorizontalOverflow &&
        !(canScrollVertically && hasVerticalOverflow)
    );
}

function updateAllTabulatorScrollbars(root) {
    root.querySelectorAll('.tabulator .tabulator-tableholder').forEach(
        (tableholder) => {
            const instance = instances.get(tableholder);

            if (!shouldUseCustomScrollbar(tableholder)) {
                if (instance) {
                    instance.destroy();
                    instances.delete(tableholder);
                    tableholder.classList.remove('tabulator-custom-scrollbar');
                    tableholder.classList.remove(
                        'tabulator-has-horizontal-scroll',
                    );
                    tableholder
                        .querySelector(
                            `:scope > .${TABULATOR_HORIZONTAL_SPACER_CLASS}`,
                        )
                        ?.remove();
                }
                return;
            }

            if (instance) {
                updateTabulatorHorizontalScrollClass(tableholder);
                instance.update();
                return;
            }

            tableholder.classList.add('tabulator-custom-scrollbar');
            updateTabulatorHorizontalScrollClass(tableholder);
            createPerfectScrollbar(tableholder);
        },
    );
}

function updateAllHorizontalScrollbars(root) {
    const candidates = new Set([
        ...root.querySelectorAll('.table-tabs.scrollable-tabs'),
        ...root.querySelectorAll('*'),
    ]);

    candidates.forEach((element) => {
        const instance = instances.get(element);

        if (instance) {
            instance.update();
            return;
        }

        if (element.matches('.table-tabs.scrollable-tabs')) {
            element.classList.add('scrollable-tabs-custom-scrollbar');
        } else if (!isNativeHorizontalScrollable(element)) {
            return;
        }

        element.classList.add('horizontal-custom-scrollbar');
        createPerfectScrollbar(element, {
            suppressScrollY: true,
            useBothWheelAxes: true,
        });
    });
}

function updateAllCustomScrollbars(root = document) {
    updateAllTabulatorScrollbars(root);
    updateAllHorizontalScrollbars(root);
}

function scheduleScrollbarUpdate() {
    if (updateFrame) return;

    updateFrame = window.requestAnimationFrame(() => {
        updateFrame = null;
        updateAllCustomScrollbars();
    });
}

function getOuterTableholder(element) {
    const row = element.closest('.tabulator-row');
    if (!row) return null;

    return row.closest('.tabulator-tableholder');
}

function initializeNestedTableWheelForwarding() {
    if (nestedWheelHandlerAttached) return;
    nestedWheelHandlerAttached = true;

    document.addEventListener(
        'wheel',
        (event) => {
            const nestedTable = event.target.closest('.nested-table');
            if (!nestedTable) return;

            const outerTableholder = getOuterTableholder(nestedTable);
            if (!outerTableholder) return;

            outerTableholder.scrollTop += event.deltaY;
            outerTableholder.dispatchEvent(
                new Event('scroll', { bubbles: true }),
            );
            event.preventDefault();
            scheduleScrollbarUpdate();
        },
        { passive: false },
    );
}

export function initializeTabulatorScrollbars() {
    updateAllCustomScrollbars();
    initializeNestedTableWheelForwarding();

    if (!observer) {
        observer = new MutationObserver(scheduleScrollbarUpdate);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        document.addEventListener(
            'tabulatorNestedTableToggled',
            scheduleScrollbarUpdate,
        );
        window.addEventListener('resize', scheduleScrollbarUpdate);
    }
}
